import { yupResolver } from '@hookform/resolvers/yup';
import { DocumentType, EmployerDocumentDto } from '@zorro/clients';
import { ERROR_MESSAGES, VALIDATION_MESSAGES } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { Select } from '@zorro/design-system/v2/Select';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { parseDateISO } from '@zorro/shared/formatters';
import { DeepReadonly } from '@zorro/types';
import { FormErrorMessage, Grid, Text } from '@zorro/web/design-system';
import {
  createEmployerDocumentUrl,
  showErrorNotification,
  useForm,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FileUploader } from '../FileUploader';
import { FormFooter } from '../FormFooter';
import { FullPageLoader } from '../FullPageLoader';
import { UploadedFileBox } from '../UploadedFileBox';
import {
  buildFileName,
  getFileNameExtension,
  getFileNameSchema,
  getFileNameStem,
} from './employerDocumentForm.utils';

/**
 * `fileName` holds only the editable stem — the extension is pinned beside the
 * input and re-attached on save, so it cannot be edited away or swapped.
 */
/**
 * Room for the pinned extension. Left to itself the v2 TextInput sizes its right
 * section for a single icon, so anything wider than `.pdf` would overflow that
 * box and run under the name. Sized for the longest we expect — `.docx`,
 * `.xlsx`, `.jpeg` — plus the section's own 12px inset.
 */
const FILE_EXTENSION_SECTION_WIDTH = 56;

const getUploadFileSchema = (
  isEditMode: boolean,
  ownFileName: string,
  existingFileNames?: string[]
) =>
  isEditMode
    ? yup.object({
        document: yup.mixed<File>().optional(),
        fileName: getFileNameSchema(ownFileName, existingFileNames),
        description: yup.string().optional().nullable(),
        openEnrollmentPeriodId: yup.string().optional(),
      })
    : yup.object({
        document: yup
          .mixed<File>()
          .required(VALIDATION_MESSAGES.documentRequired),
        fileName: getFileNameSchema(ownFileName, existingFileNames),
        description: yup.string().optional().nullable(),
        openEnrollmentPeriodId: yup
          .string()
          .required(VALIDATION_MESSAGES.planYearRequired),
      });

export type UploadDocumentProps = {
  employerId: string;
  existingDocument?: EmployerDocumentDto;
  type: DocumentType;
  viewedOpenEnrollmentPeriodId?: string;
  uploadedFile?: File;
  /** Names of every document on the tab, to reject a duplicate file name. */
  existingFileNames?: string[];
  onSuccess: () => Promise<void> | void;
  onCancel: () => Promise<void> | void;
};

export type EmployerDocumentFormFields = yup.InferType<
  ReturnType<typeof getUploadFileSchema>
>;

const uploadLabel: DeepReadonly<{
  [documentType in DocumentType]: string;
}> = {
  [DocumentType.ADMIN]: 'Document',
  [DocumentType.ZORRO_PAY]: 'Report file',
  [DocumentType.PLAN]: 'Document',
  [DocumentType.COMPLIANCE]: 'Report file',
  [DocumentType.INVOICE]: 'Document',
};

export function EmployerDocumentForm({
  employerId,
  existingDocument,
  type,
  viewedOpenEnrollmentPeriodId,
  existingFileNames,
  onSuccess,
  onCancel,
  uploadedFile,
}: UploadDocumentProps) {
  const ownFileName = existingDocument?.fileName ?? '';
  const {
    handleSubmit,
    getValues,
    control,
    watch,
    setValue,
    formState: { isValid, isDirty, errors },
  } = useForm<EmployerDocumentFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getUploadFileSchema(
        Boolean(existingDocument),
        ownFileName,
        existingFileNames
      )
    ),
    defaultValues: {
      document: uploadedFile,
      fileName: getFileNameStem(uploadedFile?.name ?? ownFileName),
      openEnrollmentPeriodId:
        existingDocument?.openEnrollmentPeriodId ??
        viewedOpenEnrollmentPeriodId,
      description: existingDocument?.description,
    },
  });
  const [shouldOverrideExistingFile, setShouldOverrideExistingFile] =
    useState(!existingDocument);
  const { startLoading, stopLoading } = useLoadingOverlay();

  const { tryMutate: createDocument } = useMonolithMutation({
    method: 'employerDocumentControllerPostDocument',
    successMessage: 'Document uploaded successfully!',
  });

  const { tryMutate: updateDocument } = useMonolithMutation({
    method: 'employerDocumentControllerUpdateDocument',
    successMessage: 'Document updated successfully!',
  });

  const { data: openEnrollmentPeriods = [], isLoading } = useMonolithQuery({
    method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
    params: [employerId],
  });

  const document = watch('document');

  // Whichever file the extension should be taken from. Undefined while an
  // existing document is waiting to be replaced — there is no settled type yet.
  const pinnedFileName =
    document?.name ??
    (shouldOverrideExistingFile ? undefined : existingDocument?.fileName);

  const handleDocumentRemoval = () => {
    setValue('document', undefined, { shouldValidate: true });
    setShouldOverrideExistingFile(Boolean(existingDocument));
  };

  const uploadDocument = async () => {
    try {
      startLoading();
      if (existingDocument) {
        let documentUrl = existingDocument.documentUrl;
        // The extension always comes from the file on disk; only the stem is
        // the user's to edit, so it is re-attached rather than typed.
        let uploadedName = existingDocument.fileName;
        if (shouldOverrideExistingFile) {
          if (!document) {
            showErrorNotification({
              message: ERROR_MESSAGES.NO_FILE_SELECTED_ERROR_MESSAGE,
            });
            return;
          }
          const { documentUrl: newDocumentUrl, fileName: newFileName } =
            await createEmployerDocumentUrl(document);
          documentUrl = newDocumentUrl;
          uploadedName = newFileName;
        }
        const { openEnrollmentPeriodId, description, fileName } = getValues();
        const updateDocumentResult = await updateDocument([
          existingDocument.id,
          {
            documentUrl,
            fileName: buildFileName(fileName, uploadedName),
            description,
            openEnrollmentPeriodId,
          },
        ]);
        if (updateDocumentResult.isOk()) {
          await onSuccess();
        }
      } else {
        if (!document) {
          showErrorNotification({
            message: ERROR_MESSAGES.NO_FILE_SELECTED_ERROR_MESSAGE,
          });
          return;
        }
        const { documentUrl, fileName: uploadedName } =
          await createEmployerDocumentUrl(document);
        const { openEnrollmentPeriodId, description, fileName } = getValues();
        const createDocumentResult = await createDocument([
          employerId,
          {
            type,
            documentUrl,
            fileName: buildFileName(fileName, uploadedName),
            description,
            openEnrollmentPeriodId,
          },
        ]);
        if (createDocumentResult.isOk()) {
          await onSuccess();
        }
      }
    } finally {
      stopLoading();
    }
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: handleSubmit(uploadDocument),
          disabled: !isValid || !isDirty,
        }}
        secondaryLabel="Cancel"
        secondaryButtonProps={{ onClick: onCancel }}
      />
    ),
    [handleSubmit, uploadDocument, isValid, isDirty, onCancel]
  );
  useDrawerFooter(drawerFooter);

  if (isLoading || !openEnrollmentPeriods) {
    return <FullPageLoader />;
  }

  return (
    <Grid>
      <Grid.Col span={12}>
        {!document && (shouldOverrideExistingFile || !existingDocument) && (
          <FileUploader
            control={control as unknown as Control}
            name="document"
            label={uploadLabel[type]}
            isLoading={false}
            onDrop={(files: File[], onBlur, onChange) => {
              onChange(files[0]);
              // Follow the file, so a replacement never leaves the old name
              // pointing at the new upload.
              setValue('fileName', getFileNameStem(files[0]?.name), {
                shouldValidate: true,
                shouldDirty: true,
              });
              onBlur();
            }}
            isRequired
            isMultiple={false}
            zoneLabelText="Or drag it here"
            buttonText="Upload a file"
          />
        )}
        {document && (
          <UploadedFileBox
            onClickDelete={handleDocumentRemoval}
            name={document.name}
            label={uploadLabel[type]}
            isRequired
          />
        )}
        {existingDocument && !shouldOverrideExistingFile && (
          <UploadedFileBox
            onClickDelete={handleDocumentRemoval}
            name={existingDocument.fileName}
            label={uploadLabel[type]}
            isRequired
          />
        )}
      </Grid.Col>
      {pinnedFileName && (
        <Grid.Col span={12}>
          <Controller
            control={control}
            name="fileName"
            render={({ field }) => (
              <TextInput
                {...field}
                label="File name"
                required
                // The extension sits outside the input, so it can be neither
                // edited away nor swapped for another file type.
                rightSection={
                  <Text c="zorroGray.4">
                    {getFileNameExtension(pinnedFileName)}
                  </Text>
                }
                rightSectionWidth={FILE_EXTENSION_SECTION_WIDTH}
              />
            )}
          />
          <FormErrorMessage fieldName="fileName" errors={errors} />
        </Grid.Col>
      )}
      <Grid.Col span={12}>
        <Controller
          control={control}
          name="description"
          render={({ field: { ref: _ref, value, ...rest } }) => (
            <>
              <Textarea
                {...rest}
                value={value || ''}
                label="Description"
                placeholder="Type description..."
                minRows={3}
                autosize
              />
              <FormErrorMessage fieldName="description" errors={errors} />
            </>
          )}
        />
      </Grid.Col>
      <Grid.Col span={12}>
        <Controller
          control={control}
          name="openEnrollmentPeriodId"
          render={({ field }) => (
            <Select
              {...field}
              required={!existingDocument}
              label="Plan year"
              placeholder="Select plan year"
              data={openEnrollmentPeriods.map((openEnrollmentPeriod) => ({
                value: openEnrollmentPeriod.id,
                label: parseDateISO(openEnrollmentPeriod.effectiveFrom)
                  .year()
                  .toString(),
              }))}
            />
          )}
        />

        <FormErrorMessage fieldName="openEnrollmentPeriodId" errors={errors} />
      </Grid.Col>
    </Grid>
  );
}
